import { Capacitor } from '@capacitor/core';

export interface DeepLinkRoute {
  path: string;
  params?: Record<string, string>;
}

const WATCHLIST_HOSTS = new Set(['watchlistcrew.com', 'www.watchlistcrew.com']);

/**
 * Maps an incoming app / universal link to a React Router location string (path + query).
 * Used by Capacitor only — must match in-app routes (/open/movie/..., /review/..., etc.).
 */
export function externalAppUrlToRouterPath(rawUrl: string): string | null {
  try {
    const url = new URL(rawUrl);
    let path: string;

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      path = `/${url.host}${url.pathname}`.replace(/\/{2,}/g, '/');
    } else if (url.protocol === 'https:' && WATCHLIST_HOSTS.has(url.hostname)) {
      path = `${url.pathname}${url.search || ''}`;
    } else {
      return null;
    }

    path = path.replace(/^\/+/, '/');
    if (!path || path === '/') {
      return null;
    }
    return path;
  } catch {
    return null;
  }
}

/** Last native deep link we applied (debounce duplicate delivery). */
let lastAppliedNativeDeepLink: { path: string; at: number } | null = null;

/** Every path we've applied this session — used so iOS re-deliveries don't yank a moved-on user back. */
const appliedThisSession = new Set<string>();

/**
 * True if we are already showing the destination of a /review/:id link on the movie page
 * (avoids iOS redelivering the universal link after Firestore/network churn).
 */
export function isRedundantReviewDeepLink(incomingPathWithQuery: string): boolean {
  try {
    const currentPath = window.location.pathname;
    const currentParams = new URLSearchParams(window.location.search);
    const pathOnly = incomingPathWithQuery.split('?')[0];
    const m = pathOnly.match(/^\/review\/([^/]+)\/?$/);
    if (!m) return false;
    const reviewId = decodeURIComponent(m[1]);
    if (currentParams.get('highlightReview') !== reviewId) return false;
    const onMovie =
      /^\/open\/movie\/[^/]+\/?$/.test(currentPath) || /^\/movie\/[^/]+\/?$/.test(currentPath);
    return onMovie;
  } catch {
    return false;
  }
}

/** Same URL delivered twice (cold start + appUrlOpen, or network churn) within this window is ignored. */
export function isNativeDeepLinkDebounced(path: string, debounceMs = 3000): boolean {
  const now = Date.now();
  return !!(
    lastAppliedNativeDeepLink &&
    lastAppliedNativeDeepLink.path === path &&
    now - lastAppliedNativeDeepLink.at < debounceMs
  );
}

/** Call only when navigation to this path is actually performed (not when queuing for flush). */
export function markNativeDeepLinkHandled(path: string): void {
  lastAppliedNativeDeepLink = { path, at: Date.now() };
  appliedThisSession.add(path);
}

/** True if this path was already applied earlier in the same session — beyond the 3s debounce. */
export function wasAlreadyAppliedThisSession(path: string): boolean {
  return appliedThisSession.has(path);
}

/**
 * Set to true when the app was cold-started or foregrounded via a shared/deep link.
 * Back navigation on these pages should always go to the homepage, not window.history.back(),
 * because the shared item is the first (and only) entry on the history stack.
 */
let _openedViaDeepLink = false;

export function markOpenedViaDeepLink(): void {
  _openedViaDeepLink = true;
}

/** True if this session began from a shared/universal link (cleared after first back navigation). */
export function wasOpenedViaDeepLink(): boolean {
  return _openedViaDeepLink;
}

/** Call this after consuming the flag so subsequent back presses behave normally. */
export function clearDeepLinkOpenedFlag(): void {
  _openedViaDeepLink = false;
}

/**
 * Service for handling deep links and universal links
 * Supports both custom URL schemes (watchlists://) and Universal Links (https://watchlistcrew.com)
 */
class DeepLinkService {
  private listeners: Set<(route: DeepLinkRoute) => void> = new Set();
  private initialized = false;

  /**
   * Initialize deep link listening
   * Should be called once when the app starts
   */
  initialize(): void {
    if (this.initialized) return;

    this.initialized = true;

    // Native: appUrlOpen is handled in capacitor-init + setDeepLinkNavigator to avoid
    // duplicate handlers (was causing full reload + SPA fight → wrong route / home).
    if (Capacitor.isNativePlatform()) {
      console.log('🔗 Deep links on native: use capacitor-init + setDeepLinkNavigator');
    } else {
      console.log('🔗 Running on web platform, deep links handled by browser routing');
    }
  }

  /**
   * Parse a URL into a route object
   * Handles both custom schemes (watchlists://movie/123) and universal links (https://watchlistcrew.com/movie/123)
   */
  private parseUrl(url: string): DeepLinkRoute | null {
    try {
      const parsedUrl = new URL(url);

      // Handle custom scheme: watchlists://
      if (parsedUrl.protocol === 'watchlists:') {
        const path = parsedUrl.pathname || parsedUrl.host;
        return this.extractRoute(path, parsedUrl.searchParams);
      }

      // Handle universal links: https://watchlistcrew.com/...
      if (
        parsedUrl.protocol === 'https:' &&
        (parsedUrl.hostname === 'watchlistcrew.com' || parsedUrl.hostname === 'www.watchlistcrew.com')
      ) {
        return this.extractRoute(parsedUrl.pathname, parsedUrl.searchParams);
      }

      console.warn('🔗 Unrecognized URL scheme:', url);
      return null;
    } catch (error) {
      console.error('🔗 Failed to parse URL:', url, error);
      return null;
    }
  }

  /**
   * Extract route information from path and search params
   */
  private extractRoute(path: string, searchParams: URLSearchParams): DeepLinkRoute | null {
    // Remove leading/trailing slashes
    path = path.replace(/^\/+|\/+$/g, '');

    if (!path) {
      return { path: '/' };
    }

    // Parse query parameters
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return {
      path: `/${path}`,
      params: Object.keys(params).length > 0 ? params : undefined
    };
  }

  /**
   * Add a listener for deep link events
   * Returns a function to remove the listener
   */
  addListener(callback: (route: DeepLinkRoute) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of a new route
   */
  private notifyListeners(route: DeepLinkRoute): void {
    console.log('🔗 Notifying listeners of route:', route);
    this.listeners.forEach(listener => {
      try {
        listener(route);
      } catch (error) {
        console.error('🔗 Error in deep link listener:', error);
      }
    });
  }

  /**
   * Generate a shareable link for a route
   * Returns a universal link for native platforms, regular URL for web
   */
  generateShareableLink(path: string, params?: Record<string, string>): string {
    const baseUrl = 'https://watchlistcrew.com';
    const url = new URL(path, baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }

  /**
   * Generate a custom scheme link (for testing or specific use cases)
   */
  generateCustomSchemeLink(path: string, params?: Record<string, string>): string {
    const url = new URL(`watchlists://${path.replace(/^\/+/, '')}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    return url.toString();
  }

  /**
   * Clean up listeners
   */
  destroy(): void {
    this.listeners.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const deepLinkService = new DeepLinkService();
